import React, { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { Metadata } from "../shared/models/metadata.model";
import { BookState } from "./state-models";

export enum BookActionName {
    UPDATE_PATH = "UPDATE_PATH",
    UPDATE_CONTENT = "UPDATE_CONTENT",
    UPDATE_METADATA = "UPDATE_METADATA",
}

/** Path resolution from URL + site; `bookSerializeToUrl` drives URL query sync for `book`. */
export type BookPathResolutionPayload = {
    bookPath?: string;
    bookSerializeToUrl: boolean;
};

export type BookStateAction =
    | { type: BookActionName.UPDATE_PATH; payload: BookPathResolutionPayload }
    | { type: BookActionName.UPDATE_CONTENT; payload: BookState["bookContent"] }
    | { type: BookActionName.UPDATE_METADATA; payload: BookState["bookMetadata"] };

export const bookStateReducer = (state: BookState, action: BookStateAction): BookState => {
    switch (action.type) {
        case BookActionName.UPDATE_PATH: {
            const nextPath = action.payload.bookPath?.trim() ? action.payload.bookPath : undefined;
            const nextSer = action.payload.bookSerializeToUrl;
            if (state.bookPath === nextPath && state.bookSerializeToUrl === nextSer) return state;
            if (state.bookPath === nextPath) {
                return { ...state, bookSerializeToUrl: nextSer };
            }
            if (!nextPath) {
                return {
                    ...state,
                    bookPath: undefined,
                    bookSerializeToUrl: nextSer,
                    bookMetadata: undefined,
                    bookContent: undefined,
                    bookLookupSettled: true,
                };
            }
            return {
                ...state,
                bookPath: nextPath,
                bookSerializeToUrl: nextSer,
                bookMetadata: undefined,
                bookContent: undefined,
                bookLookupSettled: false,
            };
        }
        case BookActionName.UPDATE_CONTENT:
            return state.bookContent === action.payload ? state : { ...state, bookContent: action.payload };
        case BookActionName.UPDATE_METADATA:
            return { ...state, bookMetadata: action.payload, bookLookupSettled: true };
        default:
            return state;
    }
};

type BookContextType = {
    bookState: BookState;
    actions: Record<BookActionName, (...args: any[]) => void>;
} | null;

export const BookStateContext = createContext<BookContextType | undefined>(undefined);

type BookStateProviderProps = {
    children: React.ReactNode;
    initialState?: BookState;
};

export const BookStateProvider = ({ children, initialState = {} }: BookStateProviderProps) => {
    const memoizedInitialState = useMemo(() => initialState, [initialState]);
    const [state, dispatch] = useReducer(bookStateReducer, memoizedInitialState);

    const actions: Record<BookActionName, (...args: any[]) => void> = useMemo(() => ({
        [BookActionName.UPDATE_PATH]: (props: BookPathResolutionPayload) =>
            dispatch({ type: BookActionName.UPDATE_PATH, payload: props }),
        [BookActionName.UPDATE_CONTENT]: (props: string) => dispatch({ type: BookActionName.UPDATE_CONTENT, payload: props }),
        [BookActionName.UPDATE_METADATA]: (props: Metadata | undefined) =>
            dispatch({ type: BookActionName.UPDATE_METADATA, payload: props }),
    }), []);

    const contextValue = useMemo(() => ({ bookState: state, actions }), [state, actions]);
    return <BookStateContext.Provider value={contextValue}>{children}</BookStateContext.Provider>;
};

export const useBookContext = () => {
    const context = useContext(BookStateContext);
    return context || { bookState: {}, actions: {} as Record<BookActionName, (...args: any[]) => void> };
};

export const useBookPath = () => useBookContext().bookState.bookPath;
export const useBookSerializeToUrl = () => useBookContext().bookState.bookSerializeToUrl === true;
export const useBookContent = () => useBookContext().bookState.bookContent;
export const useBookMetadata = () => useBookContext().bookState.bookMetadata;

export const useBookAction = (actionName: BookActionName): ((...args: any[]) => void) => {
    const context = useContext(BookStateContext);
    if (!context) {
        throw new Error("useBookAction must be used within a BookStateProvider");
    }
    return useCallback((...args: any[]) => context.actions[actionName](...args), [context.actions, actionName]);
};
